import { useState } from 'react';
import { GradientDropdownStrength } from '@/components/generic/dropdown/GradientDropdown';
import {
  IroScopePicker_NegativeImpactFragment,
  IroScopePicker_PositiveImpactFragment,
  ScopeValue,
  useUpdateIroScopeMutation,
} from '@/graphql/generated';
import { useToast } from '@/components/layout/Toast';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n';
import { useRefetchMaterialities } from './useRefetchMaterialities';
import { IroPickerDropdown } from '@/components/project/iro/evaluation/picker/IroPickerDropdown';

export type IroScopeItem = {
  id: ScopeValue | null;
  name: string;
  strength: GradientDropdownStrength;
};

const scopeCriteriaChoicesMap = (): Map<ScopeValue | null, IroScopeItem> =>
  new Map([
    [
      ScopeValue.Widespread,
      {
        id: ScopeValue.Widespread,
        name: i18n.t(`enum:iroCriteria.scope.${ScopeValue.Widespread}`),
        strength: GradientDropdownStrength.VeryStrong,
      },
    ],
    [
      ScopeValue.Large,
      {
        id: ScopeValue.Large,
        name: i18n.t(`enum:iroCriteria.scope.${ScopeValue.Large}`),
        strength: GradientDropdownStrength.Strong,
      },
    ],
    [
      ScopeValue.Limited,
      {
        id: ScopeValue.Limited,
        name: i18n.t(`enum:iroCriteria.scope.${ScopeValue.Limited}`),
        strength: GradientDropdownStrength.Medium,
      },
    ],
    [
      ScopeValue.Local,
      {
        id: ScopeValue.Local,
        name: i18n.t(`enum:iroCriteria.scope.${ScopeValue.Local}`),
        strength: GradientDropdownStrength.Light,
      },
    ],
    [
      null,
      {
        id: null,
        name: '-',
        strength: GradientDropdownStrength.VeryLight,
      },
    ],
  ]);

export const scopeCriteriaChoices = () =>
  Array.from(scopeCriteriaChoicesMap().values());

export default function IroScopePicker({
  iro,
  readOnly = false,
}: {
  iro:
    | IroScopePicker_PositiveImpactFragment
    | IroScopePicker_NegativeImpactFragment;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const items = scopeCriteriaChoicesMap();
  const initialScopeItem = items.get(iro.scopeCriteria?.value || null);
  const [selected, setSelected] = useState(initialScopeItem || null);
  const [update] = useUpdateIroScopeMutation();
  const toast = useToast();
  const refetchQueries = useRefetchMaterialities();

  const chooseItemId = (itemId: string | null) => {
    const item = items.get(itemId as ScopeValue | null) || null;
    setSelected(item);

    update({
      variables: {
        input: {
          id: iro.id,
          scope: item?.id ?? null,
        },
      },
      refetchQueries,
    })
      .then(() => {
        toast.openToastWithMessage(
          t('toast:iro.criteria.update.success', {
            criteriaName: t('iro.criteria.scope.title'),
          }),
        );
      })
      .catch(() => {
        toast.openToastWithError(t('toast:iro.criteria.scope.update.error'));
      });
  };

  return (
    <IroPickerDropdown<IroScopeItem>
      items={items}
      selected={selected}
      chooseItemId={chooseItemId}
      assessmentStats={iro.assessmentStats?.scope ?? null}
      readOnly={readOnly}
    />
  );
}
